/* AUTO-SYNCED from PsyKit.io/components/bilateral/VideoCallOverlay.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

/**
 * components/bilateral/VideoCallOverlay.tsx — the shared 1:1 video window.
 *
 * Pure presentation: renders two MediaStreams + controls, owns NO negotiation
 * (that's use-webrtc-peer). Shared by the therapist session page and the client
 * /view, so it stays layout-neutral — `position: fixed`, never wired into the
 * session grid or BallCanvas.
 *
 * Behaves like a BROWSER WINDOW (the mental model everyone already has):
 *  - DRAG from ANYWHERE in the window (except buttons/resize grips) to move it.
 *  - RESIZE from ANY of the four corners to any exact size.
 *  - MAXIMIZE button fills the screen; click again to restore.
 *  - MINIMIZE button collapses to just the title bar; click restore to bring back.
 * The call keeps running through all of it (the window only changes layout).
 *
 * Pointer discipline: a press that lands on a <button> or a resize grip does NOT
 * start a window-drag (the grips stopPropagation; buttons are detected via
 * closest("button")). This is what makes the title-bar min/max buttons clickable
 * — capturing the pointer on the title bar otherwise swallows their click
 * (Safari enforces this strictly).
 *
 * iOS Safari: the remote tile is unmuted, and iOS blocks unmuted autoplay
 * without a gesture — so we call play() explicitly and, if blocked, show a
 * "tap to enable" target (the local self-view is muted and autoplays).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { CallStatus } from "@/hooks/use-webrtc-peer";

export interface VideoCallOverlayProps {
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  status: CallStatus;
  error: string | null;
  onEndCall: () => void;
  onRetry?: () => void;
  onSwitchCamera?: () => void;
  /** Therapist defaults bottom-right; client bottom-left (opposite its fullscreen button). */
  corner?: "bottom-right" | "bottom-left";
  /** Label under the remote tile while waiting ("client" / "your therapist"). */
  peerLabel?: string;
}

type WindowMode = "normal" | "max" | "min";
/** Resize directions: 4 corners + 4 edges. onResizeMove keys off the substrings. */
type Corner = "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";
interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const MARGIN = 16;
const MIN_W = 168;
const MIN_H = 150;
const MIN_BAR_W = 210;
const DEFAULT_W = 300;
const DEFAULT_H = 286;
const GRIP = 18; // corner hit-area size

const STATUS_LABEL: Partial<Record<CallStatus, string>> = {
  acquiring: "Starting camera…",
  connecting: "Connecting…",
  connected: "Live",
  reconnecting: "Reconnecting…",
  failed: "Connection lost",
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function StreamVideo({
  stream,
  muted,
  mirrored,
  tappable,
  className,
}: {
  stream: MediaStream | null;
  muted: boolean;
  mirrored: boolean;
  tappable: boolean;
  className: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [needsTap, setNeedsTap] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (!stream) {
      setNeedsTap(false);
      return;
    }
    el.play()
      .then(() => setNeedsTap(false))
      .catch(() => setNeedsTap(tappable));
  }, [stream, tappable]);

  const tap = useCallback(() => {
    ref.current
      ?.play()
      .then(() => setNeedsTap(false))
      .catch(() => {});
  }, []);

  return (
    <div className="relative h-full w-full">
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        draggable={false}
        className={className}
        style={mirrored ? { transform: "scaleX(-1)" } : undefined}
      />
      {needsTap && (
        <button
          onClick={tap}
          className="absolute inset-0 flex flex-col items-center justify-center gap-1 font-[var(--font-barlow)] text-[11px] uppercase tracking-[0.12em]"
          style={{ background: "rgba(8,12,20,0.78)", color: "#C9A84C" }}
        >
          <span style={{ fontSize: 22 }}>▶</span>
          Tap to enable video
        </button>
      )}
    </div>
  );
}

export function VideoCallOverlay({
  localStream,
  remoteStream,
  status,
  error,
  onEndCall,
  onRetry,
  onSwitchCamera,
  corner = "bottom-right",
  peerLabel = "the other person",
}: VideoCallOverlayProps) {
  const [mode, setMode] = useState<WindowMode>("normal");
  const [rect, setRect] = useState<Rect>(() => {
    if (typeof window === "undefined") return { left: 80, top: 80, width: DEFAULT_W, height: DEFAULT_H };
    const left = corner === "bottom-left" ? MARGIN : window.innerWidth - DEFAULT_W - MARGIN;
    const top = Math.max(MARGIN, window.innerHeight - DEFAULT_H - MARGIN);
    return { left, top, width: DEFAULT_W, height: DEFAULT_H };
  });

  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const resizeRef = useRef<{ corner: Corner; x: number; y: number; rect: Rect } | null>(null);

  // ── drag from anywhere in the window (skips buttons + resize grips) ──
  const onWindowPointerDown = (e: React.PointerEvent) => {
    if (mode === "max") return; // a maximized window doesn't move
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("[data-resize]")) return; // let them handle their own press
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onWindowPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const w = mode === "min" ? MIN_BAR_W : rect.width;
    const left = clamp(e.clientX - d.dx, 4, window.innerWidth - w - 4);
    const top = clamp(e.clientY - d.dy, 4, window.innerHeight - 40);
    setRect((r) => ({ ...r, left, top }));
  };
  const onWindowPointerUp = (e: React.PointerEvent) => {
    dragRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  // ── resize from any corner (anchors the opposite edge) ──
  const onResizeDown = (c: Corner) => (e: React.PointerEvent) => {
    e.stopPropagation();
    resizeRef.current = { corner: c, x: e.clientX, y: e.clientY, rect: { ...rect } };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onResizeMove = (e: React.PointerEvent) => {
    const s = resizeRef.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    let { left, top, width, height } = s.rect;
    if (s.corner.includes("e")) width = s.rect.width + dx;
    if (s.corner.includes("s")) height = s.rect.height + dy;
    if (s.corner.includes("w")) {
      width = s.rect.width - dx;
      left = s.rect.left + dx;
    }
    if (s.corner.includes("n")) {
      height = s.rect.height - dy;
      top = s.rect.top + dy;
    }
    // Enforce minimums while keeping the anchored (opposite) edge put.
    if (width < MIN_W) {
      if (s.corner.includes("w")) left = s.rect.left + (s.rect.width - MIN_W);
      width = MIN_W;
    }
    if (height < MIN_H) {
      if (s.corner.includes("n")) top = s.rect.top + (s.rect.height - MIN_H);
      height = MIN_H;
    }
    // Keep inside the viewport.
    left = clamp(left, 4, window.innerWidth - MIN_W - 4);
    top = clamp(top, 4, window.innerHeight - MIN_H - 4);
    width = Math.min(width, window.innerWidth - left - 4);
    height = Math.min(height, window.innerHeight - top - 4);
    setRect({ left, top, width, height });
  };
  const onResizeUp = (e: React.PointerEvent) => {
    resizeRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  // Keep the window on-screen if the viewport shrinks (phone rotate, etc.).
  useEffect(() => {
    const onResize = () =>
      setRect((r) => ({
        left: clamp(r.left, 4, Math.max(4, window.innerWidth - 80)),
        top: clamp(r.top, 4, Math.max(4, window.innerHeight - 40)),
        width: Math.min(r.width, window.innerWidth - 2 * MARGIN),
        height: Math.min(r.height, window.innerHeight - 2 * MARGIN),
      }));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const statusLabel = STATUS_LABEL[status];
  const placement: React.CSSProperties =
    mode === "max"
      ? { left: MARGIN, top: MARGIN, right: MARGIN, bottom: MARGIN }
      : mode === "min"
        ? { left: rect.left, top: rect.top, width: MIN_BAR_W }
        : { left: rect.left, top: rect.top, width: rect.width, height: rect.height };

  const selfW = mode === "max" ? 150 : Math.round(Math.min(rect.width, 360) * 0.26);

  // 4 corners (square hit-areas) + 4 edges (thin strips between the corners).
  const HANDLES: { c: Corner; style: React.CSSProperties }[] = [
    { c: "nw", style: { top: -GRIP / 2, left: -GRIP / 2, width: GRIP, height: GRIP, cursor: "nwse-resize" } },
    { c: "ne", style: { top: -GRIP / 2, right: -GRIP / 2, width: GRIP, height: GRIP, cursor: "nesw-resize" } },
    { c: "sw", style: { bottom: -GRIP / 2, left: -GRIP / 2, width: GRIP, height: GRIP, cursor: "nesw-resize" } },
    { c: "se", style: { bottom: -GRIP / 2, right: -GRIP / 2, width: GRIP, height: GRIP, cursor: "nwse-resize" } },
    { c: "n", style: { top: -3, left: GRIP, right: GRIP, height: 9, cursor: "ns-resize" } },
    { c: "s", style: { bottom: -3, left: GRIP, right: GRIP, height: 9, cursor: "ns-resize" } },
    { c: "w", style: { left: -3, top: GRIP, bottom: GRIP, width: 9, cursor: "ew-resize" } },
    { c: "e", style: { right: -3, top: GRIP, bottom: GRIP, width: 9, cursor: "ew-resize" } },
  ];

  return (
    <div
      onPointerDown={onWindowPointerDown}
      onPointerMove={onWindowPointerMove}
      onPointerUp={onWindowPointerUp}
      className="fixed z-40 flex touch-none select-none flex-col overflow-hidden rounded-lg shadow-[0_12px_32px_-8px_rgba(0,0,0,0.7)]"
      style={{
        background: "#0D1B2E",
        border: "1px solid #233D6B",
        cursor: mode === "max" ? "default" : "move",
        // Maximized must beat the app nav (inline zIndex 50, NavShell) or its own
        // "Exit full screen" bar is unreachable — maximizing becomes a trap. 55 stays
        // below LeaveSessionConfirm (z-60). PiP/min keep z-40 so the post-session
        // save sheet (z-50) still covers the call, as designed.
        // ⚠ Only meaningful when this window competes at the ROOT stacking context:
        // the app releases <main>'s z-1 context on session pages (globals.css,
        // `body:has(.session-page) main`); the demo escapes via body portals instead.
        ...(mode === "max" ? { zIndex: 55 } : {}),
        ...placement,
      }}
    >
      {/* Title bar: label (left) + window controls (right), like a browser. */}
      <div
        className="flex shrink-0 items-center justify-between gap-2 px-2 py-1"
        style={{ background: "#0B1626", borderBottom: mode === "min" ? "none" : "1px solid #233D6B" }}
      >
        <span className="flex items-center gap-1.5 truncate font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#5B7088" }}>
          <span aria-hidden>⠿</span> Video
          {statusLabel && (
            <span style={{ color: status === "failed" ? "#C84444" : status === "connected" ? "#4a7c59" : "#C9A84C" }}>· {statusLabel}</span>
          )}
        </span>

        <span className="flex shrink-0 items-center gap-0.5">
          {mode !== "min" && (
            <button
              onClick={() => setMode("min")}
              aria-label="Minimize video"
              title="Minimize"
              className="flex h-6 w-6 items-center justify-center rounded-[3px] text-[13px] leading-none transition-colors hover:bg-[#1c3252]"
              style={{ color: "#9FB2C8" }}
            >
              –
            </button>
          )}
          <button
            onClick={() => setMode((m) => (m === "normal" ? "max" : "normal"))}
            aria-label={mode === "max" ? "Exit full screen" : mode === "min" ? "Restore video" : "Maximize video to full screen"}
            title={mode === "max" ? "Exit full screen" : mode === "min" ? "Restore" : "Full screen"}
            className="flex h-6 items-center justify-center gap-1 rounded-[3px] px-1.5 text-[11px] font-medium uppercase leading-none tracking-[0.08em] transition-colors hover:bg-[#1c3252]"
            style={{ color: mode === "max" ? "#C9A84C" : "#9FB2C8" }}
          >
            {/* Maximized → a clearly-labeled way back to the BLS; otherwise a compact icon. */}
            {mode === "max" ? "❐ Exit full screen" : mode === "normal" ? "▢" : "❐"}
          </button>
        </span>
      </div>

      {/* Body + footer only when not minimized. */}
      {mode !== "min" && (
        <>
          {error ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-5 text-center">
              <span style={{ fontSize: 22 }}>⚠️</span>
              <span className="font-[var(--font-barlow)] text-[11px] leading-snug" style={{ color: "#C9A2A2" }}>
                {error}
              </span>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="mt-1 rounded-[4px] border px-3 py-1 font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.1em]"
                  style={{ borderColor: "#C9A84C", color: "#C9A84C", background: "transparent" }}
                >
                  ↻ Try again
                </button>
              )}
            </div>
          ) : (
            <div className="relative min-h-0 flex-1 bg-black">
              {remoteStream ? (
                <StreamVideo stream={remoteStream} muted={false} mirrored={false} tappable className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center px-4 text-center">
                  <span className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.12em]" style={{ color: "#7A90A8" }}>
                    Waiting for {peerLabel}…
                  </span>
                </div>
              )}

              <div
                className="absolute bottom-2 left-2 overflow-hidden rounded-[4px]"
                style={{ width: selfW, height: Math.round(selfW * 0.75), border: "1px solid #233D6B", background: "#000" }}
              >
                <StreamVideo stream={localStream} muted mirrored tappable={false} className="h-full w-full object-cover" />
              </div>
            </div>
          )}

          {/* Footer — call controls (camera flip, End). Window controls are in the title bar. */}
          <div className="flex shrink-0 items-center justify-end gap-1.5 px-2 py-1.5" style={{ borderTop: "1px solid #233D6B" }}>
            {onSwitchCamera && (
              <button
                onClick={onSwitchCamera}
                aria-label="Switch camera"
                title="Switch camera"
                className="flex items-center justify-center rounded-[4px] border px-2 py-1 font-[var(--font-barlow)] text-[11px] transition-colors"
                style={{ borderColor: "#233D6B", color: "#7A90A8", background: "transparent" }}
              >
                ⟳
              </button>
            )}
            <button
              onClick={onEndCall}
              aria-label="End video call"
              className="flex items-center gap-1 rounded-[4px] border px-2.5 py-1 font-[var(--font-barlow)] text-[9px] font-medium uppercase tracking-[0.1em] transition-colors"
              style={{ borderColor: "#8B2E2E", color: "#C84444", background: "transparent" }}
            >
              ✕ End
            </button>
          </div>
        </>
      )}

      {/* Four corner resize grips (normal mode only). Invisible hit-areas; the
          cursor signals resizability. They stopPropagation so they never start a
          window-drag. */}
      {mode === "normal" &&
        HANDLES.map(({ c, style }) => (
          <div
            key={c}
            data-resize
            onPointerDown={onResizeDown(c)}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeUp}
            aria-hidden
            className="absolute z-10 touch-none"
            style={style}
          />
        ))}
    </div>
  );
}
